/**
 * This object takes a table where rows can be selected by clicking a check box.
 * It makes it possible to click anywhere on the row to check/uncheck the box.
 * Rows where the checkbox is checked are given CSS allowing them to
 * visually stand out.
 * Each table row is expected to have a check box, and it should be the first
 * input element in the row.
 * If the table has a tbody element, all rows in the tbody will be affected.
 * If there isn't a tbody, all rows in the table will be affected.
 * "numRowsToSkip" can be specified in the options, indicating to skip the
 * first N rows in the tbody if it exists, else the table.
 *
 *
 * When the CheckableRowsTable is created, it walks the table and prepares
 * the rows, adding css and event listeners.  If rows are dynamically added to
 * the table later, each new row should be passed to the prepareRow() function.
 *
 * If the checkbox is dynamically checked or uncheck using javascript, an
 * event won't be triggered to change the CSS, so the updateClickedCss()
 * function should be called.
 */

'use strict';

var CheckableRowsTable = function (table, options) {
    this.initialize(table, options);
};

CheckableRowsTable._instanceCount = 1;
CheckableRowsTable.prototype = {

    _rowIdPrefix: 'clickableRowId_',
    _clickedCss: 'hover',
    _clickListener: null,
    _allowRangeSelection: false,
    _lastMouseDownRow: null,
    _numRowsInTable: 0,
    _rowContainer: null,

    /**
     * @param table - a table DOM element or the ID of one
     * @param options - contains the following optional settings:
     *    clickListener: a function that will be called when
     *            a checkbox is checked or unchecked.  It will be passed
     *            a single argument, the checkbox DOM element.
     *        allowRangeSelection: A boolean that if set to true, will
     *            allow the user to check (but not uncheck) a range of
     *            rows by clicking on one and releasing on another.
     *            To support this, table rows will be assigned ID values, meaning
     *            you cannot use your own ID values on the rows.
     *            Also, any rows dynamically added to the table can only
     *            be added to the end of the table, and prepareRow must be called
     *            in the order they are added.
     *        clickedCss: the CSS added to a table row if (and only if) the box is checked
     *        numRowsToSkip: Specifies the number of rows that should be treated as
     *            header rows.
     */
    /* eslint-disable complexity */
    initialize: function (table, options) {
        //Give this instance a unique prefix it can use for its rows
        this._rowIdPrefix = this._rowIdPrefix + CheckableRowsTable._instanceCount++ + '_';

        if(typeof table === 'string'){
            table = document.getElementById(table);
        }

        var numRowsToSkip = 0;
        if (options) {
            //allow the user to override the 'hightlight row' CSS, if desired
            if (options.clickedCss) {
                this._clickedCss = options.clickedCss;
            }

            if (options.clickListener) {
                this._clickListener = options.clickListener;
            }

            if (options.allowRangeSelection) {
                this._allowRangeSelection = options.allowRangeSelection;
            }

            if (options.numRowsToSkip) {
                numRowsToSkip = options.numRowsToSkip;
            }
        }

        if (table != null) {
            //skip the header rows, if any, by getting rows from the tbody
            this._rowContainer = table.getElementsByTagName('tbody')[0];

            if (this._rowContainer == null) {
                this._rowContainer = table;
            }

            var rows = this._rowContainer.getElementsByTagName('tr');
            if (rows != null) {
                for (var i = numRowsToSkip; i < rows.length; i++) {
                    this.prepareRow(rows[i]);
                }
            }

            //We observe mouse down events for the whole page so that
            //nothing will happen if the user mousedown's outside the
            //table and the releases on a row.
            jQuery(document).on('mousedown', function (event) {
                var targetElement = event.target;
                //If the target is in the table, find the row
                //that was clicked
                if (jQuery.contains(table, targetElement)) {
                    this._lastMouseDownRow = MITS.Util.getParent(targetElement, 'tr');
                } else {
                    this._lastMouseDownRow = null;
                }
            }.bind(this));
        }
    },
    /* eslint-enable complexity */

    /**
     * Note: don't call this function more than once for the same
     * row. It would add multiple listeners to the row, which would
     * step on each other.
     */
    prepareRow: function (row) {
        var $row = jQuery(row);
        row.style.cursor = 'default';

        if (this._allowRangeSelection) {
            this._numRowsInTable++;
            row.id = this._rowIdPrefix + this._numRowsInTable;
        }

        var checkbox = this._getCheckboxFromRow(row);

        if (checkbox == null) {
            $row.removeClass(this._clickedCss);
            return;
        }

        //Toggle the checkbox when the row has a mouse up.
        //(We don't use click because we are stopping those events on
        // the checkbox.  See below.)
        $row.on('mouseup', function () {
            return this._handleRowClick(row);
        }.bind(this));

        //We want double clicks to function the same as a single click.
        //When a double click is triggered, there are also two normal
        //click events. On the double click event, we'll just toggle
        //a third time to get back to the single click state, which is a lot easier
        //than adding complex logic to make sure we only toggle once.
        $row.on('dblclick', function () {
            return this._handleRowClick(row);
        }.bind(this));


        // Because the toggle logic is happening in the row's event listener,
        // we need to suppress the browser's normal behavior of toggling the
        // checkbox when the checkbox is clicked.
        jQuery(checkbox).on('click', function (event) {
            //Note that for radio-buttons on FireFox,
            //the browser is doing its UI update before
            //this method is called, which means we can't stop it.
            //For selecting a radio button, it's ok for the browser
            //to select it again.  For unselecting, we don't
            //allow ourselves to unselect in _toggleRowSelection() below,
            //and let the browser re-select the already selected radio button.
            //This means we can't unselect a selected radio button, but that's
            //normal behavior. Bugzid: 34644
            event.stopPropagation();

            return false;
        }.bind(this));

        this.updateClickedCss(row);
    },

    /**
     * Checks the state of the checkbox, and updates the CSS to highlight
     * or un-highlight the row.  Either the row or the checkbox can
     * be passed in.
     */
    updateClickedCss: function (rowOrCheckbox) {
        var checkbox, row;

        if (typeof rowOrCheckbox.checked == 'undefined') {
            row = rowOrCheckbox;
            checkbox = this._getCheckboxFromRow(row);
        } else {
            checkbox = rowOrCheckbox;
            row = MITS.Util.getParent(checkbox, 'tr');
        }

        if (checkbox.checked) {
            jQuery(row).addClass(this._clickedCss);
        } else {
            jQuery(row).removeClass(this._clickedCss);
        }
    },

    uncheckAllRows: function () {
        var rows = this._rowContainer.getElementsByTagName('tr');

        for (var i = 0; i < rows.length; i++) {
            var checkbox = this._getCheckboxFromRow(rows[i]);
            if (checkbox && checkbox.checked) {
                checkbox.checked = !checkbox.checked;
                this.updateClickedCss(checkbox);
                if (this._clickListener != null) {
                    this._clickListener(checkbox);
                }
            }
        }
    },

    checkAllRows: function () {
        var rows = this._rowContainer.getElementsByTagName('tr');

        for (var i = 0; i < rows.length; i++) {
            var checkbox = this._getCheckboxFromRow(rows[i]);
            if (checkbox && !checkbox.checked) {
                if (checkbox.type !== 'radio') {
                    this._toggleRowSelection(rows[i]);
                }
            }
        }
    },

    _getCheckboxFromRow: function (row) {
        return row.getElementsByTagName('input')[0];
    },

    /**
     * Determines if a single row or range of rows was clicked on.
     */
    _handleRowClick: function (row) {
        if (this._lastMouseDownRow === row) {
            this._toggleRowSelection(row);
        }
        else if (this._allowRangeSelection && this._lastMouseDownRow != null) {
            var row1Index = this._lastMouseDownRow.id.substring(this._rowIdPrefix.length);
            var row2Index = row.id.substring(this._rowIdPrefix.length);

            //Convert the Strings to numbers so we get accurate comparison results
            row1Index = parseInt(row1Index);
            row2Index = parseInt(row2Index);


            if (row1Index < row2Index) {
                var firstIndex = row1Index;
                var lastIndex = row2Index;
            }
            else {
                var firstIndex = row2Index;
                var lastIndex = row1Index;
            }

            for (var i = firstIndex; i <= lastIndex; i++) {
                var row = document.getElementById(this._rowIdPrefix + i);
                var checkbox = this._getCheckboxFromRow(row);
                if (!checkbox.checked) {
                    this._toggleRowSelection(row);
                }
            }

            this._unselectTheSelectedText();
        }
    },

    _toggleRowSelection: function (row) {
        var checkbox = this._getCheckboxFromRow(row);
        if (checkbox.type === 'radio') {
            if (checkbox.checked) {
                //Don't allow unselecting a radio button
                //because we are unable to stop the normal
                //browser behavior up a above on the click event,
                //which selects it again. See bugzid: 34644
                return;
            }
            else {
                //We're about to select a radio button.
                //Clear the currently selected radio button, if any
                this.uncheckAllRows();
            }
        }

        //Toggle the checkbox and the CSS
        checkbox.checked = !checkbox.checked;
        this.updateClickedCss(checkbox);
        if (this._clickListener != null) {
            this._clickListener(checkbox);
        }
    },

    _unselectTheSelectedText: function () {
        if (document.selection) {
            document.selection.empty();
        }
        else if (window.getSelection) {
            window.getSelection().removeAllRanges();
        }
    }
};

module.exports = CheckableRowsTable;



//////////////////
// WEBPACK FOOTER
// ./mits/Widgets/CheckableRowsTable.js
// module id = 18
// module chunks = 0